import {action, makeObservable, observable} from "mobx";

import {
  GetTicketReportRequest,
  GetUserReportRequest,
  PartnerBetDetailReportRequest,
  PartnerBetDetailReportResponse,
  PartnerBetReportByGroup,
  PartnerBetReportChartRequest,
  PartnerBetReportChartResponse,
  PartnerBetReportRequest,
  PartnerBetReportResponse,
  PartnerBetReportsByGroupRequest,
  PartnerBetReportsByGroupResponse,
  TicketReportResponse,
  UserReportResponse,
} from "../models";
import {DEFAULT_RESPONSE_LIST, GroupTypeParamNames} from "../constants";
import {HttpResponse, reportsController} from "../api";
import {GroupTypesEnum} from "../enums";
import {RootStore} from "./index";

export class ReportsStore {
  rootStore: RootStore;

  public userReport = new HttpResponse<Partial<UserReportResponse>>({});

  public ticketReport = new HttpResponse<Partial<TicketReportResponse>>({});

  public reportTotalInfo = new HttpResponse<Partial<TicketReportResponse>>({});

  public partnerBetReport = new HttpResponse<PartnerBetReportResponse>(DEFAULT_RESPONSE_LIST);

  public betReports = new HttpResponse<PartnerBetReportsByGroupResponse>(DEFAULT_RESPONSE_LIST);

  public betSubReports = new HttpResponse<PartnerBetReportsByGroupResponse>(DEFAULT_RESPONSE_LIST);

  public betSubReportsMap = <Record<string, any>>{};

  public partnerBetDetailReport = new HttpResponse<PartnerBetDetailReportResponse>(DEFAULT_RESPONSE_LIST);

  public partnerBetChartReport = new HttpResponse<PartnerBetReportChartResponse>([]);

  public partnerPayoutChartReport = new HttpResponse<PartnerBetReportChartResponse>([]);

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      userReport: observable,
      ticketReport: observable,
      reportTotalInfo: observable,
      partnerBetReport: observable,
      partnerBetDetailReport: observable,
      betReports: observable,
      betSubReports: observable,
      betSubReportsMap: observable,
      partnerBetChartReport: observable,
      partnerPayoutChartReport: observable,
      setBetSubReportsMap: action.bound,
      setUserReport: action.bound,
      setTicketReport: action.bound,
      setReportTotalInfo: action.bound,
      setPartnerBetReport: action.bound,
      setPartnerBetDetailReport: action.bound,
      setBetReports: action.bound,
      setBetSubReports: action.bound,
      setPartnerBetChartReport: action.bound,
      setPartnerPayoutChartReport: action.bound,
      resetAll: action.bound,
    });
  }

  public setUserReport(value: HttpResponse<Partial<UserReportResponse>>): void {
    this.userReport = value;
  }

  public setBetSubReportsMap(value: Record<string, any>): void {
    this.betSubReportsMap = value;
  }

  public setTicketReport(
    value: HttpResponse<Partial<TicketReportResponse>>,
  ): void {
    this.ticketReport = value;
  }

  public setReportTotalInfo(
    value: HttpResponse<Partial<TicketReportResponse>>,
  ): void {
    this.reportTotalInfo = value;
  }

  public setPartnerBetReport(value: HttpResponse<PartnerBetReportResponse>): void {
    this.partnerBetReport = value;
  }

  public setPartnerBetDetailReport(value: HttpResponse<PartnerBetDetailReportResponse>): void {
    this.partnerBetDetailReport = value;
  }

  public setBetReports(value: HttpResponse<PartnerBetReportsByGroupResponse>): void {
    this.betReports = value;
  }

  public setBetSubReports(value: HttpResponse<PartnerBetReportsByGroupResponse>): void {
    this.betSubReports = value;
  }

  public setPartnerBetChartReport(value: HttpResponse<PartnerBetReportChartResponse>): void {
    this.partnerBetChartReport = value;
  }

  public setPartnerPayoutChartReport(value: HttpResponse<PartnerBetReportChartResponse>): void {
    this.partnerPayoutChartReport = value;
  }

  public resetAll(): void {
    this.setUserReport(this.userReport.fetching());
    this.setTicketReport(this.ticketReport.fetching());
  }

  public removeAllData(): void {
    this.setUserReport(new HttpResponse<Partial<UserReportResponse>>({}));
    this.setTicketReport(new HttpResponse<Partial<TicketReportResponse>>({}));
  }

  public async getUserReport(params?: GetUserReportRequest): Promise<void> {
    this.setUserReport(this.userReport.fetching());
    const data = await reportsController.getUserReport(params);
    this.setUserReport(this.userReport.fetched(data));
  }

  public async getTicketReport(params?: GetTicketReportRequest): Promise<void> {
    this.setTicketReport(this.ticketReport.fetching());
    const data = await reportsController.getTicketReport(params);
    this.setTicketReport(this.ticketReport.fetched(data));
  }

  public async getReportTotalInfo(params?: GetTicketReportRequest): Promise<void> {
    this.setReportTotalInfo(this.reportTotalInfo.fetching());
    const data = await reportsController.getTicketReport(params);
    this.setReportTotalInfo(this.reportTotalInfo.fetched(data));
  }

  public async getPartnerBetReports(params?: PartnerBetReportRequest): Promise<void> {
    this.setPartnerBetReport(this.partnerBetReport.fetching());
    const data = await reportsController.getPartnerBetReports(params);
    this.setPartnerBetReport(this.partnerBetReport.fetched(data));
  }

  public async getPartnerBetDetailReports(params: PartnerBetDetailReportRequest): Promise<void> {
    this.setPartnerBetDetailReport(this.partnerBetDetailReport.fetching());
    const result = await reportsController.getPartnerBetDetailReports(params);
    this.setPartnerBetDetailReport(this.partnerBetDetailReport.fetched(result));
  }

  public async getBetReports(params: PartnerBetReportsByGroupRequest): Promise<void> {
    this.setBetReports(this.betReports.fetching());
    const data = await reportsController.getPartnerBetReportsByGroup(params);
    this.setBetReports(this.betReports.fetched(data));
  }

  public async getBetSubReports(params: PartnerBetReportsByGroupRequest): Promise<void> {
    const { type, id, ...restParams } = params;

    const isManagerGroupType = type === GroupTypesEnum.Manager;

    this.setBetSubReports(this.betSubReports.fetching());

    const data = await reportsController.getPartnerBetReportsByGroup(isManagerGroupType ? {...restParams, groupBy: GroupTypesEnum.Manager } : restParams);

    let dataBetshopsWithoutManager: PartnerBetReportByGroup[] = [];
    let dataSubManagers: PartnerBetReportByGroup[] = [];

    if (type === GroupTypesEnum.BusinessUnit) {
       const data = await reportsController.getPartnerBetReportsByGroup({...restParams, betshopsWithoutManager: true })
       dataBetshopsWithoutManager = data.items;
    }
    if (type === GroupTypesEnum.Manager) {
      const data = await reportsController.getPartnerBetReportsByGroup({...restParams, groupBy: GroupTypesEnum.Betshop, expand: true })
      dataSubManagers = data.items;
    }
    this.setBetSubReports(this.betSubReports.fetched(data));
    this.setBetSubReportsMap({...this.betSubReportsMap, [GroupTypeParamNames[type as GroupTypesEnum] + id]:[ ...data.items, ...dataBetshopsWithoutManager, ...dataSubManagers]});
  }

  public async getPartnerBetChartReport(params: PartnerBetReportChartRequest): Promise<void> {
    this.setPartnerBetChartReport(this.partnerBetChartReport.fetching());
    const data = await reportsController.getPartnerBetChartReport(params);
    this.setPartnerBetChartReport(this.partnerBetChartReport.fetched(data));
  }

  public async getPartnerPayoutChartReport(params: PartnerBetReportChartRequest): Promise<void> {
    this.setPartnerPayoutChartReport(this.partnerPayoutChartReport.fetching());
    const data = await reportsController.getPartnerPayoutChartReport(params);
    this.setPartnerPayoutChartReport(this.partnerPayoutChartReport.fetched(data));
  }


  public resetPartnerBetDetailReport(): void {
    this.setPartnerBetDetailReport(this.partnerBetDetailReport.fetched(DEFAULT_RESPONSE_LIST));
  }

  public removeBetSubReportMap(groupType: string) {
    delete this.betSubReportsMap[groupType];
    this.betSubReportsMap = {...this.betSubReportsMap}
  }

  public resetReportTotalInfo(): void {
    this.setReportTotalInfo(this.reportTotalInfo.fetched({}));
  }

}
